import React from 'react';
import {
    Attestations,
    FOC,
    GapTypeTabs,
    OutgoingReferrals,
    PatientPageStatusType,
    Quality,
    Referrals,
} from '~/components/Patient';
import { Card } from '~/components/Card';
import { ServicesContext } from '~/services';
import { ReferralProvider } from '~/services/ReferralContext';

interface GapFeedProps {
    patient: PatientType;
    page: string;
    setPage: (page: string) => void;
    status: PatientPageStatusType;
    setStatus: (newPage: string, newStatus: number) => void;
}
export function GapFeed({ patient, page, setPage, status, setStatus }: GapFeedProps) {
    const context = React.useContext(ServicesContext);
    const [externalUpload, setExternalUpload] = React.useState<null | ExternalAttachmentType>(null);

    React.useEffect(() => {
        function pageListener(request: MessageEvent<ExternalAttachmentEventType>) {
            if (request.source !== window || request.origin !== window.location.origin) return;
            if (request.data.method === 'documentPrint') {
                if (request.data.data.destination === 'zephyr-attestation') {
                    setPage('risk_gaps');
                    setExternalUpload(request.data.data);
                } else if (request.data.data.destination === 'zephyr-quality') {
                    setPage('quality_gaps');
                    setExternalUpload(request.data.data);
                }
            }
        }
        window.addEventListener('message', pageListener);
        return () => {
            window.removeEventListener('message', pageListener);
        };
    }, [setPage]);

    const clearExternalUpload = React.useCallback(() => {
        setExternalUpload(null);
    }, []);

    return (
        <Card
            header={<GapTypeTabs page={page} setPage={setPage} patient={patient} />}
            className="mb-3"
        >
            {page === 'risk_gaps' && (
                <Attestations
                    patient={patient}
                    status={status.risk_gaps}
                    setStatus={setStatus}
                    externalUpload={externalUpload}
                    clearExternalUpload={clearExternalUpload}
                />
            )}
            {page === 'foc_gaps' && <FOC patient={patient} status={status.foc_gaps} />}
            {page === 'quality_gaps' && (
                <Quality
                    patient={patient}
                    status={status.quality_gaps}
                    externalUpload={externalUpload}
                    clearExternalUpload={clearExternalUpload}
                />
            )}
            {context.user.hasFeatureToggle('referrals') && (
                <ReferralProvider show={page === 'referrals'}>
                    <Referrals patient={patient} show={page === 'referrals'} />
                </ReferralProvider>
            )}
            {page === 'outgoing_referrals' && <OutgoingReferrals patient={patient} />}
        </Card>
    );
}
